import React, { forwardRef, type ForwardedRef } from 'react';
import { clsx } from 'clsx';
import './RadioGroup.scss';

export interface ESSRadioOption {
  value: string;
  label: string;
}

export interface ESSRadioGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Label for the radio group */
  label?: React.ReactNode;
  /** Helper text to display below the radio group */
  helperText?: string;
  /** Whether the radio group is in an error state */
  isInvalid?: boolean;
  /** Error message to display when isInvalid is true */
  errorMessage?: string;
  /** Whether the radio buttons should be displayed horizontally */
  orientation?: 'horizontal' | 'vertical';
  /** Name for the radio group */
  name: string;
  /** Options for the radio group */
  options?: ESSRadioOption[];
  /** Current value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
}

export const RadioGroup = forwardRef<HTMLDivElement, ESSRadioGroupProps>(
  function RadioGroup(
    {
      label,
      helperText,
      isInvalid,
      errorMessage,
      className,
      orientation = 'vertical',
      name,
      options = [],
      value,
      onChange,
      ...props
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const groupClasses = clsx(
      'ess-radio-group',
      `ess-radio-group--${orientation}`,
      {
        'ess-radio-group--invalid': isInvalid,
      },
      className,
    );

    return (
      <div ref={ref} className={groupClasses} {...props}>
        {label && <div className="ess-radio-group__label">{label}</div>}
        <div className="ess-radio-group__options">
          {options.map((option) => (
            <label key={option.value} className="ess-radio-group__option">
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange?.(e.target.value)}
                className="ess-radio-group__input"
                aria-invalid={isInvalid}
              />
              <span className="ess-radio-group__control" />
              <span className="ess-radio-group__option-label">
                {option.label}
              </span>
            </label>
          ))}
        </div>
        {(helperText || (isInvalid && errorMessage)) && (
          <div className="ess-radio-group__help-text">
            {isInvalid && errorMessage ? errorMessage : helperText}
          </div>
        )}
      </div>
    );
  },
);

RadioGroup.displayName = 'ESSRadioGroup';
